// Components
import { EventChip } from 'app/modules/alerts/components/AlertDetails/EventChip';

// Models
import {
  U21TableColumn,
  U21TableColumnTypes,
  U21TableColumnTypesType,
} from 'app/shared/u21-ui/components';
import {
  U21ActionEventChip,
  U21EntityChip,
  U21InstrumentChip,
} from 'app/shared/u21-ui/components/dashboard';
import { U21DataChipType } from 'app/shared/u21-ui/components/dashboard/dataChip/U21DataChip';
import { CGDOIdPair } from 'app/shared/models';
import {
  AssociatedAlert,
  AssociatedCase,
  AssociatedSAR,
} from 'app/modules/alerts/types';

const LIST: U21TableColumnTypesType = 'list';

/**
 * Union type for all row types that can appear in Prior Activity tables.
 */
type PriorActivityRow = AssociatedAlert | AssociatedCase | AssociatedSAR;

/**
 * Column IDs for Prior Activity table columns.
 * These are exported as constants for use in other modules that need to reference
 * the column IDs without instantiating the column definitions.
 */
export const ENTITY_IDS_COLUMN_ID = 'entity_ids';

/**
 * Helper function to safely pair external IDs with internal IDs.
 * Always pairs all internal IDs, using external IDs when available.
 * When external IDs are missing, display components will show fallback labels
 * (e.g., "Entity #123"). This ensures backwards compatibility with endpoints
 * that may return empty external ID arrays while internal IDs have values.
 */
const pairExternalAndInternalIds = (
  externalIds: string[] | undefined,
  internalIds: (number | string)[] | undefined,
): CGDOIdPair[] => {
  const external = externalIds || [];
  const internal = internalIds || [];

  // Always pair all internal IDs, use external IDs when available
  return internal.map((internalId, i) => ({
    externalId: external[i] || '', // Empty string triggers fallback in display components
    internalId,
  }));
};

/**
 * Creates a column definition for entity IDs that works with any Prior Activity row type.
 * This factory function ensures type safety without requiring type assertions.
 */
export const createEntityIdsColumn = <
  TRow extends PriorActivityRow,
>(): U21TableColumn<TRow, CGDOIdPair[]> => ({
  id: ENTITY_IDS_COLUMN_ID,
  accessor: (row: TRow): CGDOIdPair[] => {
    return pairExternalAndInternalIds(row.entity_external_ids, row.entity_ids);
  },
  Header: 'Entities',
  disableSortBy: true,
  type: LIST,
  cellProps: {
    getShowLessText: () => 'Show fewer entities',
    getShowMoreText: (length: number) => `Show all ${length} entities`,
    displayFunc: (item: CGDOIdPair) => (
      <U21EntityChip
        key={item.internalId}
        id={item.internalId}
        name={item.externalId || `Entity #${item.internalId}`}
      />
    ),
  },
});

/**
 * Creates a column definition for event IDs that works with Case or SAR row types.
 * This factory function ensures type safety without requiring type assertions.
 */
export const createEventIdsColumn = <
  TRow extends AssociatedCase | AssociatedSAR,
>(): U21TableColumn<TRow, CGDOIdPair[]> => ({
  id: 'event_ids',
  accessor: (row: TRow): CGDOIdPair[] => {
    return pairExternalAndInternalIds(row.event_external_ids, row.event_ids);
  },
  Header: 'Transactions',
  disableSortBy: true,
  type: LIST,
  cellProps: {
    displayFunc: (item: CGDOIdPair) => (
      <EventChip
        key={item.internalId}
        eventID={item.internalId}
        externalID={item.externalId}
      />
    ),
  },
});

/**
 * Creates a column definition for action event IDs that works with Case row types.
 * This factory function ensures type safety without requiring type assertions.
 */
export const createActionEventIdsColumn = <
  TRow extends AssociatedCase,
>(): U21TableColumn<TRow, CGDOIdPair[]> => ({
  id: 'action_event_ids',
  accessor: (row: TRow): CGDOIdPair[] => {
    return pairExternalAndInternalIds(
      row.action_event_external_ids,
      row.action_event_ids,
    );
  },
  Header: 'Action Events',
  disableSortBy: true,
  type: LIST,
  cellProps: {
    displayFunc: (item: CGDOIdPair) => (
      <U21ActionEventChip
        key={item.internalId}
        id={item.internalId}
        label={item.externalId || `Action Event #${item.internalId}`}
        type={U21DataChipType.ACTION_EVENT}
      />
    ),
  },
});

/**
 * Creates a column definition for instrument IDs that works with Alert or Case row types.
 * This factory function ensures type safety without requiring type assertions.
 */
export const createInstrumentIdsColumn = <
  TRow extends AssociatedAlert | AssociatedCase,
>(): U21TableColumn<TRow, CGDOIdPair[]> => ({
  id: 'instrument_ids',
  accessor: (row: TRow): CGDOIdPair[] => {
    return pairExternalAndInternalIds(
      row.instrument_external_ids,
      row.instrument_ids,
    );
  },
  Header: 'Instruments',
  disableSortBy: true,
  type: LIST,
  cellProps: {
    getShowLessText: () => 'Show fewer instruments',
    getShowMoreText: (length: number) => `Show all ${length} instruments`,
    displayFunc: (item: CGDOIdPair) => (
      <U21InstrumentChip
        key={item.internalId}
        id={item.internalId}
        label={item.externalId || `Instrument #${item.internalId}`}
      />
    ),
  },
});

/**
 * Creates a column definition for created_at that works with any Prior Activity row type.
 * This factory function ensures type safety without requiring type assertions.
 */
export const createCreatedAtColumn = <
  TRow extends PriorActivityRow,
>(): U21TableColumn<TRow, string> => ({
  id: 'created_at',
  accessor: 'created_at',
  Header: 'Created at',
  type: U21TableColumnTypes.DATETIME,
});
